import { useEffect } from 'react';
import { ACCESS_LEVELS } from '../../../../constants/value';
import translations from '../../../../translations';
import type { FormalValueReference, SubflowArgument } from '../../../../types/graph';
import type { ValueElementIdAPI, ValueElementIdReferenceAPI } from '../../../../types/value';
import Table, { type TableColumnList } from '../../../generic/Table';
import Loader from '../../../loader/Loader';
import { useNotificationDispatch } from '../../../notifications/NotificationProvider';
import ValueSelectorModal from '../../../values/selector/ValueSelectorModal';
import { useMapElement } from '../contextProviders/MapElementProvider';
import { generateSubFlowArgumentSelectors } from './subflowUtils';

type Props = {
    setSubFlowArguments: (subflowArguments: FormalValueReference[], access: string) => void;
    subFlowId: string;
    valueReferences: ValueElementIdReferenceAPI[];
    access: keyof typeof ACCESS_LEVELS;
    subFlowArguments: SubflowArgument[];
    subFlowArgumentsToBeRendered: FormalValueReference[];
    onSelect: (value: ValueElementIdAPI | null, id: string) => void;
    isLoading: boolean;
};

const InputOutputValues = ({
    setSubFlowArguments,
    subFlowId,
    valueReferences,
    access,
    subFlowArguments,
    subFlowArgumentsToBeRendered,
    onSelect,
    isLoading,
}: Props) => {
    const { container } = useMapElement();

    const dispatchNotification = useNotificationDispatch();

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        getTableContents();
    }, [subFlowId]);

    const getTableContents = () => {
        generateSubFlowArgumentSelectors(access, subFlowId, valueReferences, subFlowArguments)
            .then((subFlowArgumentsSelectors) =>
                setSubFlowArguments(subFlowArgumentsSelectors, access),
            )
            .catch((error) => {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            });
    };

    // We are only interested in displaying the values of the specified access type
    const rows = subFlowArgumentsToBeRendered.filter(
        (subFlowArgument) => subFlowArgument.access === access,
    );

    const columns: TableColumnList<FormalValueReference> = [
        {
            renderHeader: () => translations.COMMON_TABLE_accepted_value,
            renderCell: ({ item }) => item.formalValueName,
        },
        {
            renderHeader: () => ACCESS_LEVELS[access].description,
            renderCell: ({ item }) =>
                ACCESS_LEVELS[access] === ACCESS_LEVELS['PUBLIC'] ? (
                    ACCESS_LEVELS[access].description
                ) : (
                    <ValueSelectorModal
                        value={item.selectedValueMeta}
                        onChange={(value) => {
                            onSelect(value, item.id);
                        }}
                        container={container}
                        includeSystemValues={false}
                    />
                ),
        },
    ];

    return (
        <div>
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    <h2>{ACCESS_LEVELS[access].labelShort}</h2>
                    <Table
                        key={ACCESS_LEVELS[access].label}
                        columns={columns}
                        items={rows}
                        isLoading={isLoading}
                        pagination={true}
                    />
                </>
            )}
        </div>
    );
};

export default InputOutputValues;
